import React, { useEffect, useRef, useState } from "react";
import SnpButton from "../global/SnpButton";
import DialogBox from "../global/DialogBox";
import PrinterConfig from "./PrinterConfig";

export const SILENT_PRINT_DECISION_EVENT = "snp-silent-print-decision";

export default function SilentPrintDecisionManager() {
  const requestRef = useRef(null);
  const [request, setRequest] = useState(null);
  const [configuring, setConfiguring] = useState(false);
  const [checking, setChecking] = useState(false);
  const [retryError, setRetryError] = useState("");

  useEffect(() => {
    const listener = (event) => {
      if (requestRef.current?.resolve) requestRef.current.resolve("dismiss");
      requestRef.current = event.detail;
      setRequest(event.detail);
      setConfiguring(false);
      setChecking(false);
      setRetryError("");
    };
    window.addEventListener(SILENT_PRINT_DECISION_EVENT, listener);
    return () => window.removeEventListener(SILENT_PRINT_DECISION_EVENT, listener);
  }, []);

  const finish = (decision) => {
    requestRef.current?.resolve?.(decision);
    requestRef.current = null;
    setRequest(null);
    setConfiguring(false);
    setChecking(false);
    setRetryError("");
  };

  const configurationSaved = async () => {
    setChecking(true);
    setRetryError("");
    try {
      const printed = await requestRef.current?.retry?.();
      if (printed) finish("handled");
      else finish("pdf");
    } catch (error) {
      setConfiguring(false);
      setRetryError(error?.message || "The printer is still unavailable.");
    } finally {
      setChecking(false);
    }
  };

  if (!request) return null;

  const dialogContent = configuring ? (
    <>
      {checking && <p style={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>Configuration saved. Checking the Printer Agent and printing…</p>}
      <PrinterConfig embedded onSaved={configurationSaved} />
    </>
  ) : (
    <p style={{ whiteSpace: "normal", overflowWrap: "anywhere", lineHeight: 1.6, margin: 0 }}>
      {retryError || request.message || "The configured printer is unavailable."}{" "}
      You can configure the printer, print this document as a PDF, or dismiss without printing.
    </p>
  );

  const dialogActions = configuring ? null : (
    <>
      <SnpButton variant="contained" onClick={() => finish("dismiss")}>Dismiss</SnpButton>
      <SnpButton variant="contained" onClick={() => finish("pdf")}>Print via PDF</SnpButton>
      {request.allowConfigure !== false && (
        <SnpButton variant="contained" color="primary" onClick={() => setConfiguring(true)}>Configure Printer</SnpButton>
      )}
    </>
  );

  return (
    <DialogBox
      showDialog
      title={configuring ? "Printer Configuration" : "Printer is not configured"}
      dialogContent={dialogContent}
      dialogActions={dialogActions}
      onClose={() => finish("dismiss")}
      outsideClickedDismiss={false}
      mwidth={configuring ? "lg" : "sm"}
      mFullWidth
    />
  );
}
